import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Alert, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
    FormControlLabel, Grid, InputLabel, MenuItem, Select, TextField,
} from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import EnergyAdminApiService from '../../../api/EnergyAdminApiService';
import SaveButton from '../../Common/SaveButton';
import { useSnackbar } from '../../Common/SnackbarProvider';
import { useAsyncCallback } from '../../../hooks/useAsyncCallback';
import {
    COMMODITY_OPTIONS, DATA_SOURCE_OPTIONS, EnergyCommodity, EnergyDataSource, MeterResolution,
    RESOLUTION_OPTIONS,
} from '../../../constants/energyConstants';

const energyAdminApiService = new EnergyAdminApiService();

const EMPTY = {
    energyUtilityId: '',
    commodity: EnergyCommodity.Electricity,
    defaultUnitId: '',
    accountNumber: '',
    meterNumber: '',
    resolution: MeterResolution.BilledOnly,
    dataSource: EnergyDataSource.Manual,
    isActive: true,
};

function toForm (meter) {
    if (!meter) return { ...EMPTY };

    return {
        energyUtilityId: meter.energyUtilityId,
        commodity: meter.commodity,
        defaultUnitId: meter.defaultUnitId,
        accountNumber: meter.accountNumber ?? '',
        meterNumber: meter.meterNumber ?? '',
        resolution: meter.resolution,
        dataSource: meter.dataSource,
        isActive: meter.isActive,
    };
}

/**
 * Creates or edits a meter: one utility account for one commodity. An account billing both gas and
 * electricity is two meters sharing an account number, so each can be baselined on its own.
 */
export default function EditEnergyMeterModal ({ open, siteId, currentMeter, utilities, units, onCancel, onSave }) {
    const { t } = useTranslation();
    const snackbar = useSnackbar();

    const [form, setForm] = useState({ ...EMPTY });

    useEffect(() => {
        if (open) {
            setForm(toForm(currentMeter));
        }
    }, [open, currentMeter]);

    const update = (field, value) => setForm(current => ({ ...current, [field]: value }));

    // The server rejects a unit that measures something else, so only offer the ones it will take.
    const unitOptions = useMemo(
        () => units.filter(unit => unit.commodityId === form.commodity || unit.commodityId === EnergyCommodity.Other),
        [units, form.commodity]);

    useEffect(() => {
        if (unitOptions.length > 0 && !unitOptions.some(unit => unit.id === form.defaultUnitId)) {
            update('defaultUnitId', unitOptions[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitOptions]);

    const commodityLocked = !!currentMeter && currentMeter.billCount > 0;

    const { run: handleSave, loading: saving } = useAsyncCallback(async () => {
        if (!form.accountNumber.trim()) {
            snackbar.error(t('energySetup.validation.accountRequired'));
            return;
        }

        if (!form.energyUtilityId) {
            snackbar.error(t('energySetup.validation.utilityRequired'));
            return;
        }

        // With no units loaded the select is empty, and posting '' would come back as a bare 400.
        if (!form.defaultUnitId) {
            snackbar.error(t('energySetup.validation.unitRequired'));
            return;
        }

        const payload = {
            energyUtilityId: form.energyUtilityId,
            commodity: form.commodity,
            defaultUnitId: form.defaultUnitId,
            accountNumber: form.accountNumber.trim(),
            meterNumber: form.meterNumber || null,
            resolution: form.resolution,
            dataSource: form.dataSource,
            isActive: form.isActive,
        };

        const response = currentMeter
            ? await energyAdminApiService.updateMeter(currentMeter.id, payload)
            : await energyAdminApiService.createMeter(siteId, payload);

        if (!response?.success) {
            snackbar.error(response?.message ?? t('energySetup.toast.saveFailed'));
            return;
        }

        snackbar.success(currentMeter
            ? t('energySetup.toast.meterUpdated', { account: payload.accountNumber })
            : t('energySetup.toast.meterCreated', { account: payload.accountNumber }));

        onSave();
    });

    return (
        <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
            <DialogTitle>
                {currentMeter
                    ? t('energySetup.meter.editTitle', { account: currentMeter.accountNumber })
                    : t('energySetup.meter.createTitle')}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="meter-utility">{t('energySetup.field.utility')}</InputLabel>
                            <Select
                                labelId="meter-utility"
                                label={t('energySetup.field.utility')}
                                value={form.energyUtilityId}
                                onChange={event => update('energyUtilityId', event.target.value)}
                            >
                                {utilities.map(utility => (
                                    <MenuItem key={utility.id} value={utility.id}>{utility.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth size="small" disabled={commodityLocked}>
                            <InputLabel id="meter-commodity">{t('energySetup.field.commodity')}</InputLabel>
                            <Select
                                labelId="meter-commodity"
                                label={t('energySetup.field.commodity')}
                                value={form.commodity}
                                onChange={event => update('commodity', event.target.value)}
                            >
                                {COMMODITY_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{t(option.labelKey)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {commodityLocked && (
                        <Grid item xs={12}>
                            <Alert severity="info">{t('energySetup.help.commodityLocked', { count: currentMeter.billCount })}</Alert>
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 60 }}
                            label={t('energySetup.field.accountNumber')}
                            helperText={t('energySetup.help.accountNumber')}
                            value={form.accountNumber}
                            onChange={event => update('accountNumber', event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            fullWidth
                            size="small"
                            inputProps={{ maxLength: 60 }}
                            label={t('energySetup.field.meterNumber')}
                            value={form.meterNumber}
                            onChange={event => update('meterNumber', event.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="meter-unit">{t('energySetup.field.defaultUnit')}</InputLabel>
                            <Select
                                labelId="meter-unit"
                                label={t('energySetup.field.defaultUnit')}
                                value={unitOptions.some(unit => unit.id === form.defaultUnitId) ? form.defaultUnitId : ''}
                                onChange={event => update('defaultUnitId', event.target.value)}
                            >
                                {unitOptions.map(unit => (
                                    <MenuItem key={unit.id} value={unit.id}>{unit.code}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="meter-resolution">{t('energySetup.field.resolution')}</InputLabel>
                            <Select
                                labelId="meter-resolution"
                                label={t('energySetup.field.resolution')}
                                value={form.resolution}
                                onChange={event => update('resolution', event.target.value)}
                            >
                                {RESOLUTION_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{t(option.labelKey)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth size="small">
                            <InputLabel id="meter-source">{t('energySetup.field.dataSource')}</InputLabel>
                            <Select
                                labelId="meter-source"
                                label={t('energySetup.field.dataSource')}
                                value={form.dataSource}
                                onChange={event => update('dataSource', event.target.value)}
                            >
                                {DATA_SOURCE_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{t(option.labelKey)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    {form.resolution === MeterResolution.Interval && (
                        <Grid item xs={12}>
                            <Alert severity="info">{t('energySetup.help.intervalMeter')}</Alert>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={form.isActive}
                                    onChange={event => update('isActive', event.target.checked)}
                                />
                            )}
                            label={t('common.active')}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={saving}>{t('common.cancel')}</Button>
                <SaveButton variant="contained" loading={saving} onClick={handleSave}>{t('common.save')}</SaveButton>
            </DialogActions>
        </Dialog>
    );
}

EditEnergyMeterModal.propTypes = {
    open: PropTypes.bool.isRequired,
    siteId: PropTypes.number,
    currentMeter: PropTypes.object,
    utilities: PropTypes.array.isRequired,
    units: PropTypes.array.isRequired,
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
};
